import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

const defaultDateFormatter = () =>
  new Date().toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });

const defaultTimeFormatter = () =>
  new Date().toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

type DateTimeDisplayProps = {
  intervalMs?: number;
  formatDate?: () => string;
  formatTime?: () => string;
  textColor?: string;
  sx?: SxProps<Theme>;
};

export const DateTimeDisplay: React.FC<DateTimeDisplayProps> = ({
  intervalMs = 1000,
  formatDate = defaultDateFormatter,
  formatTime = defaultTimeFormatter,
  textColor = 'text.secondary',
  sx,
}) => {
  const [currentDate, setCurrentDate] = useState(() => formatDate());
  const [currentTime, setCurrentTime] = useState(() => formatTime());

  useEffect(() => {
    const id = window.setInterval(() => {
      setCurrentDate(formatDate());
      setCurrentTime(formatTime());
    }, intervalMs);

    return () => window.clearInterval(id);
  }, [formatDate, formatTime, intervalMs]);

  const combinedSx = useMemo<SxProps<Theme>>(() => {
    const base: SxProps<Theme> = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      color: textColor,
      lineHeight: 1.1,
    };

    if (!sx) return base;
    return Array.isArray(sx) ? [base, ...sx] : [base, sx];
  }, [sx, textColor]);

  return (
    <Box sx={combinedSx}>
      <Typography variant="caption" component="div" sx={{ color: 'inherit' }}>
        {currentDate}
      </Typography>
      <Typography
        variant="subtitle2"
        component="time"
        dateTime={currentTime}
        sx={{ color: 'inherit' }}
      >
        {currentTime}
      </Typography>
    </Box>
  );
};
